import { apiClient } from './client'

// ============ TYPE DEFINITIONS ============

export interface UserStats {
  total: number
  active: number
  inactive: number
  students: number
  teachers: number
  parents: number
  drivers: number
  byRole: Record<string, number>
}

export interface LibraryStats {
  totalBooks: number
  totalCopies: number
  availableCopies: number
  activeBorrowings: number
  overdueBorrowings: number
}

export interface FleetStats {
  totalVehicles: number
  activeVehicles: number
}

export interface AttendanceStats {
  presentCount: number
  absentCount: number
  lateCount: number
  attendanceRate: number
}

export interface WeeklyLibraryData {
  day: string
  borrowed: number
  returned: number
}

export interface SchoolAdminDashboardData {
  users: UserStats
  library: LibraryStats
  fleet: FleetStats
  attendance: AttendanceStats
  charts: {
    weeklyLibrary: WeeklyLibraryData[]
  }
}

export interface StudentLibraryStats {
  activeBorrowings: number
  overdueBorrowings: number
  totalBorrowed: number
  pendingFines: {
    amount: number
    count: number
  }
}

export interface RecentBorrowing {
  _id: string
  book: {
    _id: string
    title: string
    authors: string[]
    coverImage?: string
  }
  bookCopy: {
    _id: string
    barcode: string
  }
  borrowedDate: string
  dueDate: string
  status: string
}

export interface StudentDashboardData {
  library: StudentLibraryStats
  recentBorrowings: RecentBorrowing[]
  wallet: {
    balance: number
  }
}

export interface ChildInfo {
  id: string
  firstName: string
  lastName: string
  studentId?: string
  grade?: string
  stream?: string
}

export interface ChildStats {
  childId: string
  activeBorrowings: number
  overdueBorrowings: number
  pendingFines: number
}

export interface ParentDashboardData {
  children: ChildInfo[]
  childrenCount: number
  library: {
    totalActiveBorrowings: number
    totalOverdueBorrowings: number
    totalPendingFines: number
  }
  childrenStats: ChildStats[]
  wallet: {
    balance: number
  }
}

export interface VehicleInfo {
  id: string
  registrationNumber: string
  model?: string
  status: string
  currentRoute?: string
}

export interface TripInfo {
  id: string
  route?: string
  type: string
  scheduledTime: string
  status: string
}

export interface DriverDashboardData {
  vehicle: VehicleInfo | null
  todayTrips: TripInfo[]
  stats: {
    totalTripsCompleted: number
    totalTripsToday: number
    studentsTransportedToday: number
  }
}

export interface SessionInfo {
  id: string
  lesson?: string
  stream?: string
  startTime: string
  endTime: string
  status: string
}

export interface StreamInfo {
  id: string
  name: string
  grade?: string
}

export interface TeacherDashboardData {
  todaySessions: SessionInfo[]
  classTeacherOf: StreamInfo[]
  stats: {
    totalSessionsToday: number
    completedSessionsToday: number
    pendingSessionsToday: number
    totalStudentsInClasses: number
  }
}

export interface TopInstitution {
  id: string
  name: string
  slug: string
  userCount: number
  isActive: boolean
  plan: string
}

export interface RecentInstitution {
  id: string
  name: string
  slug: string
  isActive: boolean
  plan: string
  createdAt: string
}

export interface SuperAdminDashboardData {
  overview: {
    institutions: {
      total: number
      active: number
      inactive: number
      byPlan: Record<string, number>
      byValidation: Record<string, number>
      thisMonth: number
      lastMonth: number
      growth: number
    }
    users: {
      total: number
      active: number
      inactive: number
      emailVerified: number
      byRole: Record<string, number>
      summary: {
        students: number
        teachers: number
        parents: number
        drivers: number
        schoolAdmins: number
      }
      thisMonth: number
      lastMonth: number
      today: number
      growth: number
    }
  }
  fleet: {
    totalVehicles: number
    byStatus: Record<string, number>
    byFuelType: Record<string, number>
    tracking: number
    withGPS: number
    maintenanceDue: number
    totalCapacity: number
  }
  library: {
    books: { total: number; active: number }
    copies: {
      total: number
      byStatus: Record<string, number>
      byCondition: Record<string, number>
    }
    borrowings: {
      total: number
      active: number
      overdue: number
      thisMonth: number
    }
    fines: {
      pending: { amount: number; count: number }
      collected: { amount: number; count: number }
      thisMonth: number
    }
  }
  financial: {
    transactions: {
      byType: Record<string, { total: number; count: number }>
      byCategory: Record<string, { total: number; count: number }>
      byPaymentMethod: Record<string, { total: number; count: number }>
      byStatus: Record<string, number>
      thisMonth: { revenue: number; count: number }
      lastMonth: { revenue: number }
      today: Record<string, { total: number; count: number }>
      revenueGrowth: number
    }
    wallets: {
      total: number
      active: number
      totalBalance: number
    }
    invoices: {
      byStatus: Record<string, { count: number; total: number }>
      outstanding: { amount: number; count: number }
      overdue: { amount: number; count: number }
    }
  }
  rfid: {
    terminals: {
      total: number
      byStatus: Record<string, number>
      totalScans: number
      lowBattery: number
    }
    cards: {
      total: number
      byStatus: Record<string, number>
      byType: Record<string, number>
      assigned: number
      unassigned: number
      totalScans: number
    }
  }
  attendance: {
    sessions: {
      total: number
      byStatus: Record<string, number>
      thisMonth: number
    }
    attendance: Record<string, number>
  }
  security: {
    sessions: {
      total: number
      active: number
      today: number
      byDevice: Record<string, number>
    }
    loginAttempts: {
      total: number
      failed: number
      today: number
      failedToday: number
      byReason: Record<string, number>
    }
  }
  charts: {
    userRegistration: Array<{ date: string; count: number }>
    transactions: Array<{
      date: string
      credit: number
      debit: number
      creditCount: number
      debitCount: number
    }>
  }
  topInstitutions: TopInstitution[]
  recentInstitutions: RecentInstitution[]
  generatedAt: string
}

// ============ API RESPONSE TYPES ============

export interface DashboardResponse<T> {
  success: boolean
  data: T
}

// ============ API FUNCTIONS ============

/**
 * Get School Admin Dashboard data
 * Returns aggregated stats for users, library, fleet, attendance, and charts
 */
export const getSchoolAdminDashboard = async (): Promise<DashboardResponse<SchoolAdminDashboardData>> => {
  const response = await apiClient.get('/dashboard/school-admin')
  return response.data
}

/**
 * Get Student Dashboard data
 * Returns borrowings, fines, recent borrowings, and wallet info
 */
export const getStudentDashboard = async (): Promise<DashboardResponse<StudentDashboardData>> => {
  const response = await apiClient.get('/dashboard/student')
  return response.data
}

/**
 * Get Parent Dashboard data
 * Returns children info, their borrowings/fines, and wallet balance
 */
export const getParentDashboard = async (): Promise<DashboardResponse<ParentDashboardData>> => {
  const response = await apiClient.get('/dashboard/parent')
  return response.data
}

/**
 * Get Driver Dashboard data
 * Returns vehicle info, today's trips, and driving stats
 */
export const getDriverDashboard = async (): Promise<DashboardResponse<DriverDashboardData>> => {
  const response = await apiClient.get('/dashboard/driver')
  return response.data
}

/**
 * Get Teacher Dashboard data
 * Returns today's sessions, class teacher info, and teaching stats
 */
export const getTeacherDashboard = async (): Promise<DashboardResponse<TeacherDashboardData>> => {
  const response = await apiClient.get('/dashboard/teacher')
  return response.data
}

/**
 * Get Super Admin Dashboard data
 * Returns platform-wide institution and user stats
 */
export const getSuperAdminDashboard = async (): Promise<DashboardResponse<SuperAdminDashboardData>> => {
  const response = await apiClient.get('/dashboard/super-admin')
  return response.data
}
